import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  X,
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Wallet as WalletIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { connectInjectedWallet } from '../utils/walletConnect';

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupMethod, setSignupMethod] = useState('wallet');
  const [pendingWalletConnection, setPendingWalletConnection] = useState(false);

  const [formData, setFormData] = useState({
    identifier: '',
    username: '',
    email: '',
    walletAddress: '',
    password: '',
    confirmPassword: '',
    referredBy: '',
  });

  const referralCode = searchParams.get('ref') || '';

  useEffect(() => {
    setIsLogin(initialMode === 'login');
  }, [initialMode]);

  useEffect(() => {
    if (!isLogin) {
      setSignupMethod('wallet');
    }
  }, [isLogin]);

  useEffect(() => {
    if (isOpen && !isLogin && referralCode) {
      setFormData((prev) => ({ ...prev, referredBy: referralCode }));
    }
  }, [isOpen, isLogin, referralCode]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        identifier: '',
        username: '',
        email: '',
        walletAddress: '',
        password: '',
        confirmPassword: '',
        referredBy: referralCode || '',
      });
      setPendingWalletConnection(false);
    }
  }, [isOpen, referralCode]);

  const handleWalletLogin = async (address) => {
    setIsLoading(true);
    try {
      const result = await login({ identifier: address });
      if (result.success) {
        onClose();
        navigate('/');
      }
    } finally {
      setIsLoading(false);
      setPendingWalletConnection(false);
    }
  };

  const handleConnectWallet = async () => {
    setPendingWalletConnection(true);
    try {
      const address = await connectInjectedWallet();
      if (isLogin) {
        await handleWalletLogin(address);
      } else {
        setFormData((prev) => ({ ...prev, walletAddress: address }));
        toast.success('Wallet connected! Complete your registration.');
      }
    } catch (err) {
      toast.error(err.message || 'Wallet connection failed');
    } finally {
      setPendingWalletConnection(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const result = await login({
          identifier: formData.identifier.trim(),
          password: formData.password.trim() || undefined,
        });
        if (result.success) {
          onClose();
          navigate('/');
        }
      } else {
        if (signupMethod === 'email') {
          if (!formData.password || !formData.confirmPassword) {
            toast.error('Password and confirm password are required');
            setIsLoading(false);
            return;
          }
          if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            setIsLoading(false);
            return;
          }
        }

        if (signupMethod === 'wallet' && !formData.walletAddress) {
          toast.error('Please connect your wallet');
          setIsLoading(false);
          return;
        }

        const registrationData = {
          username: formData.username.trim(),
        };

        if (formData.referredBy?.trim()) {
          registrationData.referredBy = formData.referredBy.trim();
        }

        if (signupMethod === 'email') {
          registrationData.email = formData.email.trim();
          registrationData.password = formData.password.trim();
          registrationData.confirmPassword = formData.confirmPassword.trim();
        } else {
          registrationData.walletAddress = formData.walletAddress.trim();
        }

        const result = await register(registrationData);
        if (result.success) {
          onClose();
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      identifier: '',
      username: '',
      email: '',
      walletAddress: '',
      password: '',
      confirmPassword: '',
      referredBy: referralCode || '',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-heading text-heading dark:text-white">
            {isLogin ? 'Sign In' : 'Create Account'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!isLogin && (
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setSignupMethod('wallet')}
                className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                  signupMethod === 'wallet'
                    ? 'bg-accent text-white border-accent'
                    : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Wallet
              </button>
              <button
                type="button"
                onClick={() => setSignupMethod('email')}
                className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                  signupMethod === 'email'
                    ? 'bg-accent text-white border-accent'
                    : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Email
              </button>
            </div>
          )}

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  minLength={3}
                  className="input-field input-field-icon"
                  placeholder="Choose a username"
                />
              </div>
            </div>
          )}

          {isLogin ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter your Email or username
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  type="text"
                  value={formData.identifier}
                  onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  required
                  className="input-field input-field-icon"
                  placeholder="email@example.com or username"
                  autoComplete="username"
                />
              </div>
            </div>
          ) : signupMethod === 'email' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input-field input-field-icon"
                  placeholder="Enter email address"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Wallet Address *
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <button
                  type="button"
                  onClick={handleConnectWallet}
                  disabled={isLoading || pendingWalletConnection}
                  className="btn-wallet flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 shrink-0"
                >
                  <WalletIcon className="w-4 h-4" />
                  <span>{formData.walletAddress ? 'Reconnect Wallet' : 'Connect Wallet'}</span>
                </button>
                <input
                  type="text"
                  value={formData.walletAddress}
                  readOnly
                  required
                  className="input-field flex-1 min-w-0 text-sm"
                  placeholder="Connect wallet to fill address"
                />
              </div>
              {!formData.walletAddress && (
                <p className="text-gray-500 text-xs mt-1">Click Connect Wallet to link your account</p>
              )}
            </div>
          )}

          {(isLogin || signupMethod === 'email') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password {signupMethod === 'email' && !isLogin && '*'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={signupMethod === 'email' && !isLogin}
                  minLength={signupMethod === 'email' && !isLogin ? 6 : undefined}
                  className="input-field input-field-icon input-field-icon-right"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {!isLogin && signupMethod === 'email' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  className="input-field input-field-icon"
                  placeholder="Repeat password"
                />
              </div>
            </div>
          )}

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Referral Code {referralCode && <span className="text-green-600">(Pre-filled)</span>}
              </label>
              <input
                type="text"
                value={formData.referredBy}
                onChange={(e) => setFormData({ ...formData, referredBy: e.target.value })}
                className="input-field"
                placeholder="Enter referral code"
                readOnly={!!referralCode}
                style={
                  referralCode
                    ? {
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        cursor: 'not-allowed',
                        borderColor: 'rgba(34, 197, 94, 0.5)',
                      }
                    : {}
                }
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || pendingWalletConnection}
            className="w-full btn-primary flex items-center justify-center disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                {isLogin ? 'Signing In...' : 'Creating Account...'}
              </>
            ) : isLogin ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </button>

          {isLogin && (
            <button
              type="button"
              onClick={handleConnectWallet}
              disabled={isLoading || pendingWalletConnection}
              className="w-full btn-wallet flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              <WalletIcon className="w-4 h-4" />
              <span>Connect Wallet to Sign In</span>
            </button>
          )}
        </form>

        <div className="px-6 pb-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={switchMode}
                className="text-accent hover:text-accent/80 ml-1 font-medium"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
